import { memo } from 'react';

import { useTranslation } from 'react-i18next';

import AuthGuidelinesLink from '@/components/organisms/Dialogs/Node/AuthNode/AuthGuidelinesLink';
import AuthOutcomeBox from '@/components/organisms/Dialogs/Node/AuthNode/AuthOutcomeBox';
import AuthStepQuestionPreview from '@/components/organisms/Dialogs/Node/AuthNode/AuthStepQuestionPreview';
import { AuthStepKey } from '@/components/pages/Authentication/types';
import { invalidRing } from '@/components/ui/constants';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { AuthConfig, AuthPanel, AuthStep } from '@/shared/types/node';
import { isKeyEnter } from '@/util/util';

const VerticalArrow = () => (
  <svg className="h-6 w-3 shrink-0" viewBox="0 0 12 24">
    <line x1="6" y1="0" x2="6" y2="20" className="stroke-border" strokeWidth="1.5" />
    <polygon points="2,18 6,24 10,18" className="fill-border" />
  </svg>
);

// A down arrow above each of the two outcome columns (success | failure), so
// the step visibly forks into both outcomes.
const OutcomeArrows = () => (
  <div className="flex w-full gap-2">
    <div className="flex flex-1 justify-center">
      <VerticalArrow />
    </div>
    <div className="flex flex-1 justify-center">
      <VerticalArrow />
    </div>
  </div>
);

// Connector from the success column to the centered next step. `non-scaling`
// keeps the stroke width constant while the viewBox x-coords track the column
// centers as the card resizes; the arrowhead is overlaid centered at the bottom.
const SuccessToVerifyElbow = () => (
  <div className="relative h-10 w-full" aria-hidden>
    <svg className="h-full w-full" viewBox="0 0 100 40" preserveAspectRatio="none">
      <path
        d="M25 0 V20 H50 V34"
        fill="none"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
        className="stroke-border"
      />
    </svg>
    <div className="absolute bottom-0 left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-t-[6px] border-x-transparent border-t-border" />
  </div>
);

type StepSectionProps = {
  step: AuthStepKey;
  config?: AuthStep;
  selectedPanel: AuthPanel | null;
  errorMessage?: string;
  successBoxHasError?: boolean;
  failureBoxHasError?: boolean;
  onStepClick: (e: React.MouseEvent) => void;
  onToggle: (enabled: boolean) => void;
  onSuccessClick: (e: React.MouseEvent) => void;
  onFailureClick: (e: React.MouseEvent) => void;
  onGuidelinesClick: (e: React.MouseEvent) => void;
};

const StepSection = memo(
  ({
    step,
    config,
    selectedPanel,
    errorMessage,
    successBoxHasError,
    failureBoxHasError,
    onStepClick,
    onToggle,
    onSuccessClick,
    onFailureClick,
    onGuidelinesClick,
  }: StepSectionProps) => {
    const { t } = useTranslation();
    const enabled = config?.enabled ?? false;
    const questions = config?.questions ?? [];
    const hasQuestions = questions.length > 0;
    const isIdentification = step === 'identification';

    const title = isIdentification
      ? t('dialog.auth.step_identification_title')
      : t('dialog.auth.step_verification_title');
    const subtitle = isIdentification
      ? t('dialog.auth.step_identification_subtitle')
      : t('dialog.auth.step_verification_subtitle');

    const successPanel: AuthPanel = `success-${step}`;
    const failurePanel: AuthPanel = `failure-${step}`;

    // Keep the card at a normal action width so it doesn't overlap sibling
    // branches or blow out the branch-connector geometry.
    return (
      <div className="flex w-full flex-col items-center">
        {/* Step card */}
        <div
          className={cn(
            'w-full cursor-pointer overflow-hidden rounded border bg-background p-1 transition-all',
            enabled ? 'border-border' : 'border-dashed border-muted-foreground',
            !errorMessage && enabled && 'hover:border-muted-foreground',
            selectedPanel === `step-${step}` && !errorMessage && 'border-selected',
            errorMessage && invalidRing
          )}
          role="button"
          tabIndex={0}
          onClick={onStepClick}
          onKeyUp={(e) => {
            if (isKeyEnter(e)) {
              onStepClick(e as unknown as React.MouseEvent);
            }
          }}
          data-testid={`auth-step-${step}`}
        >
          <div className={cn('rounded', enabled && 'bg-subtle')}>
            <div className="flex items-start justify-between gap-2 px-3 py-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-left text-sm font-semibold text-foreground">{title}</span>
                <span className="text-left text-xs text-muted-foreground">{subtitle}</span>
              </div>
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                <Switch checked={enabled} onCheckedChange={onToggle} />
              </div>
            </div>

            {questions.length > 0 && (
              <div className="px-3 pb-2">
                {questions.map((q, i) => (
                  <AuthStepQuestionPreview key={q.question_id} index={i} question={q} />
                ))}
                <AuthGuidelinesLink onClick={onGuidelinesClick} />
              </div>
            )}
          </div>
        </div>

        {errorMessage && <p className="mt-1 self-start text-xs text-warning">{errorMessage}</p>}

        {/* Success (left) is the continuation; failure (right) is a terminal leaf. */}
        {hasQuestions && (
          <>
            <OutcomeArrows />
            <div className="flex w-full items-stretch gap-2">
              <div className="flex min-w-0 flex-1">
                <AuthOutcomeBox
                  outcome="success"
                  enabled={enabled}
                  selected={selectedPanel === successPanel}
                  hasError={successBoxHasError}
                  onClick={onSuccessClick}
                />
              </div>
              <div className="flex min-w-0 flex-1">
                <AuthOutcomeBox
                  outcome="failure"
                  enabled={enabled}
                  selected={selectedPanel === failurePanel}
                  hasError={failureBoxHasError}
                  onClick={onFailureClick}
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

StepSection.displayName = 'StepSection';

export type AuthStepsCanvasProps = {
  authConfig: AuthConfig;
  selectedPanel: AuthPanel | null;
  identificationError?: string;
  verificationError?: string;
  successBoxHasError?: boolean;
  failureBoxHasError?: boolean;
  onSelectPanel: (panel: AuthPanel) => void;
  onToggleStep: (step: AuthStepKey, enabled: boolean) => void;
  onOpenGuidelines: (step: AuthStepKey) => void;
};

/**
 * Visualizes a sub_agent auth flow as a single narrow column of step cards,
 * each with its success/failure outcome boxes stacked beneath it. Trigger
 * header is intentionally absent — trigger defaults to `conversation_starts`
 * until UX brings back a configurable picker.
 */
const AuthStepsCanvas = ({
  authConfig,
  selectedPanel,
  identificationError,
  verificationError,
  successBoxHasError,
  failureBoxHasError,
  onSelectPanel,
  onToggleStep,
  onOpenGuidelines,
}: AuthStepsCanvasProps) => {
  const identStep = authConfig.identification_step;
  const verifyStep = authConfig.verification_step;

  // Success/failure boxes only render under the LAST enabled step (matches the
  // legacy AuthenticationPage behavior).
  const nodeSelectorsStep: AuthStepKey = verifyStep?.enabled ? 'verification' : 'identification';

  // The identification → verification arrow flows out of identification's
  // success box, so use the elbow whenever that outcomes row is present. This
  // mirrors StepSection's render condition (questions exist), independent of
  // whether the step is enabled — a configured-but-disabled step still shows
  // its outcomes.
  const identHasOutcomes = (identStep?.questions?.length ?? 0) > 0;

  return (
    <div className="flex w-full flex-col items-center">
      <StepSection
        step="identification"
        config={identStep}
        selectedPanel={selectedPanel}
        errorMessage={identificationError}
        successBoxHasError={nodeSelectorsStep === 'identification' && successBoxHasError}
        failureBoxHasError={nodeSelectorsStep === 'identification' && failureBoxHasError}
        onStepClick={() => onSelectPanel('step-identification')}
        onToggle={(enabled) => onToggleStep('identification', enabled)}
        onSuccessClick={() => onSelectPanel('success-identification')}
        onFailureClick={() => onSelectPanel('failure-identification')}
        onGuidelinesClick={() => onOpenGuidelines('identification')}
      />

      {identHasOutcomes ? <SuccessToVerifyElbow /> : <VerticalArrow />}

      <StepSection
        step="verification"
        config={verifyStep}
        selectedPanel={selectedPanel}
        errorMessage={verificationError}
        successBoxHasError={nodeSelectorsStep === 'verification' && successBoxHasError}
        failureBoxHasError={nodeSelectorsStep === 'verification' && failureBoxHasError}
        onStepClick={() => onSelectPanel('step-verification')}
        onToggle={(enabled) => onToggleStep('verification', enabled)}
        onSuccessClick={() => onSelectPanel('success-verification')}
        onFailureClick={() => onSelectPanel('failure-verification')}
        onGuidelinesClick={() => onOpenGuidelines('verification')}
      />
    </div>
  );
};

export default AuthStepsCanvas;
